import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Check, ChevronsUpDown, X } from "lucide-react";

const toOption = (d) => ({
  id: d.id,
  label: d.name || `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.id,
  sub: d.account_number || d.account_name || d.city || "",
});

function useSearch(endpoint, open, q) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const sep = endpoint.includes("?") ? "&" : "?";
        const r = await api.get(`${endpoint}${sep}q=${encodeURIComponent(q)}&limit=25`);
        let data = (r.data && (r.data.items || r.data)) || [];
        data = data.map(toOption).sort((a, b) => a.label.localeCompare(b.label));
        setItems(data);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [endpoint, open, q]);
  return { items, loading };
}

function ResultList({ items, loading, selectedIds, onPick, testId }) {
  return (
    <div className="max-h-56 overflow-y-auto py-1" data-testid={testId ? `${testId}-list` : undefined}>
      {loading && <p className="px-3 py-2 text-xs text-gray-400">…</p>}
      {!loading && items.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Keine Treffer</p>}
      {items.map((it) => (
        <button key={it.id} type="button" onClick={() => onPick(it)}
          data-testid={testId ? `${testId}-opt-${it.id}` : undefined}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-amber-50/60 transition-colors">
          <Check className={`h-3.5 w-3.5 shrink-0 ${selectedIds.includes(it.id) ? "opacity-100 text-[#1B2A4A]" : "opacity-0"}`} />
          <span className="truncate flex-1">{it.label}</span>
          {it.sub ? <span className="text-xs text-gray-400 truncate max-w-[40%]">{it.sub}</span> : null}
        </button>
      ))}
    </div>
  );
}

/** Single-select async search combobox (server-side search, no full-list load). */
export function SearchSelect({ endpoint, value, initialLabel = "", onChange, placeholder = "–", testId, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [label, setLabel] = useState(initialLabel);
  const { items, loading } = useSearch(endpoint, open, q);
  useEffect(() => setLabel(initialLabel), [initialLabel]);

  const pick = (it) => { onChange(it.id, it); setLabel(it.label); setOpen(false); setQ(""); };
  const clear = (e) => { e.stopPropagation(); onChange("", null); setLabel(""); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm hover:bg-gray-50 transition-colors">
          <span className={`truncate ${label ? "" : "text-muted-foreground"}`}>{label || placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {allowClear && value ? <X className="h-3.5 w-3.5 opacity-50 hover:opacity-100" onClick={clear} data-testid={testId ? `${testId}-clear` : undefined} /> : null}
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Input autoFocus placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)}
          className="border-0 border-b rounded-none focus-visible:ring-0"
          data-testid={testId ? `${testId}-search` : undefined} />
        <ResultList items={items} loading={loading} selectedIds={value ? [value] : []} onPick={pick} testId={testId} />
      </PopoverContent>
    </Popover>
  );
}

/** Multi-select async search combobox with chips. */
export function SearchMultiSelect({ endpoint, values = [], initialLabels = {}, onChange, placeholder = "–", testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [labels, setLabels] = useState(initialLabels);
  const { items, loading } = useSearch(endpoint, open, q);
  useEffect(() => setLabels((l) => ({ ...initialLabels, ...l })), [initialLabels]);

  const emit = (ids, lbls) => onChange(ids, lbls);
  const pick = (it) => {
    if (values.includes(it.id)) return;
    const lbls = { ...labels, [it.id]: it.label };
    setLabels(lbls);
    emit([...values, it.id], lbls);
  };
  const remove = (id) => emit(values.filter((x) => x !== id), labels);

  return (
    <div className="space-y-2" data-testid={testId}>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((id) => (
            <Badge key={id} variant="outline" className="gap-1 pr-1" data-testid={testId ? `${testId}-chip-${id}` : undefined}>
              <span className="truncate max-w-[180px]">{labels[id] || id}</span>
              <button type="button" onClick={() => remove(id)} className="rounded-full hover:bg-gray-200 p-0.5">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId ? `${testId}-add` : undefined}
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm hover:bg-gray-50 transition-colors">
            <span className="text-muted-foreground">{placeholder}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
          <Input autoFocus placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)}
            className="border-0 border-b rounded-none focus-visible:ring-0"
            data-testid={testId ? `${testId}-search` : undefined} />
          <ResultList items={items} loading={loading} selectedIds={values} onPick={pick} testId={testId} />
        </PopoverContent>
      </Popover>
    </div>
  );
}
